import {
    cloneElement,
    forwardRef,
    ImgHTMLAttributes,
    isValidElement,
    ReactElement,
    ReactNode,
    RefObject,
    useCallback,
    useRef,
    useState,
} from "react";
import MuiAvatar, { AvatarProps } from "@mui/material/Avatar";
import useForkRef from "@mui/utils/useForkRef";
import { IconAvatarPlaceholder } from "src/components/icons-mui/avatar-placeholder";
import { useElementFirstSeen } from "src/hooks/use-element-first-seen";
import { useImagePreload } from "src/hooks/use-image-preload";

export interface IWfAvatarProps extends Omit<AvatarProps, "imgProps" | "slotProps" | "children" | "alt"> {
    children?: ReactNode;
    alt?: string;
    loading?: ImgHTMLAttributes<HTMLImageElement>["loading"];
    skipImageVerification?: boolean;
}

interface IImageSourceAttributes {
    src?: string;
    srcSet?: string;
}

const useVerifiedImageSources = (
    { src, srcSet }: IImageSourceAttributes,
    isLazy: boolean,
    rootRef: RefObject<HTMLElement>
): IImageSourceAttributes => {
    const [canStartLoading, setCanStartLoading] = useState(!isLazy);

    const handleFirstSeen = useCallback(() => setCanStartLoading(true), []);
    useElementFirstSeen(rootRef, isLazy ? handleFirstSeen : undefined);

    const isLoaded = useImagePreload(canStartLoading ? src : undefined, canStartLoading ? srcSet : undefined);

    return isLoaded ? { src, srcSet } : {};
};

export const WfAvatar = forwardRef<HTMLDivElement, IWfAvatarProps>(
    ({ alt, children = <IconAvatarPlaceholder />, loading, src, srcSet, skipImageVerification = false, ...rest }, ref) => {
        const rootRef = useRef<HTMLDivElement>(null);
        const combinedRef = useForkRef(ref, rootRef);
        const verifiedSources = useVerifiedImageSources(
            skipImageVerification ? {} : { src, srcSet },
            !skipImageVerification && loading === "lazy",
            rootRef
        );
        const imageSources = skipImageVerification ? { src, srcSet } : verifiedSources;

        const fallback = isValidElement(children)
            ? cloneElement(children as ReactElement<{ role?: string; "aria-label"?: string }>, {
                  role: "img",
                  "aria-label": alt,
              })
            : children;

        return (
            <MuiAvatar ref={combinedRef} alt={alt} srcSet={imageSources.srcSet} src={imageSources.src} slotProps={{ img: { loading } }} {...rest}>
                {fallback}
            </MuiAvatar>
        );
    }
);

WfAvatar.displayName = "WfAvatar";
